import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Typography } from "@mui/material";

import CoreCard from "../../../ui/core/CoreCard";
import { useObservabilityStore } from "../../../../hooks/useObservabilityStore";
import {
  formatCurrency,
  formatNumber,
  MetricGrid,
  SectionLabel,
  type Metric,
} from "./MetricTiles";
import type { FinancialImpact } from "../../../../types/observability";

// Financial recovery story: how many claims were denied (and for how much),
// and how many we appealed. Recovered revenue lives in the Summary row above.
const buildMetrics = (impact: FinancialImpact): Metric[] => [
  { label: "Total Denied Claims", value: formatNumber(impact.denied_claims) },
  { label: "Denied Dollars", value: formatCurrency(impact.denied_dollars) },
  { label: "Claims Appealed", value: formatNumber(impact.claims_appealed) },
];

const ImpactMetricsCard: FC = observer(() => {
  const { isFinancialImpactLoading, financialImpact, loadFinancialImpact } =
    useObservabilityStore();

  useEffect(() => {
    loadFinancialImpact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CoreCard sx={{ p: 1, minHeight: "auto", height: "auto" }}>
      <SectionLabel sx={{ mb: 0.5 }}>Impact</SectionLabel>
      {isFinancialImpactLoading ? (
        <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : !financialImpact ? (
        <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No impact data available
          </Typography>
        </Box>
      ) : (
        <MetricGrid metrics={buildMetrics(financialImpact)} columns={3} />
      )}
    </CoreCard>
  );
});

export default ImpactMetricsCard;
